type PublicUrlEnvName =
  | 'NEXT_PUBLIC_CDN_BASE_URL'
  | 'NEXT_PUBLIC_REVERSE_GEOCODE_URL'
  | 'NEXT_PUBLIC_PHONE_REFRESH_TOKEN_URL'
  | 'NEXT_PUBLIC_HELP_CENTER_URL'
  | 'NEXT_PUBLIC_BLUE_DART_TRACKING_URL'
  | 'NEXT_PUBLIC_GOOGLE_PRIVACY_POLICY_URL'
  | 'NEXT_PUBLIC_GOOGLE_ANALYTICS_OPTOUT_URL'
  | 'NEXT_PUBLIC_FACEBOOK_URL'
  | 'NEXT_PUBLIC_X_URL'
  | 'NEXT_PUBLIC_LINKEDIN_URL'
  | 'NEXT_PUBLIC_INSTAGRAM_URL'
  | 'NEXT_PUBLIC_SUPPORT_EMAIL'
  | 'NEXT_PUBLIC_GRIEVANCE_EMAIL'
  | 'NEXT_PUBLIC_SUPPORT_PHONE_DISPLAY'
  | 'NEXT_PUBLIC_SUPPORT_PHONE_NUMBER'
  | 'NEXT_PUBLIC_GTM_ID'
  | 'NEXT_PUBLIC_GTM_SCRIPT_URL'
  | 'NEXT_PUBLIC_GTM_NOSCRIPT_URL'
  | 'NEXT_PUBLIC_GA_MEASUREMENT_ID'
  | 'NEXT_PUBLIC_GA_SCRIPT_URL'
  | 'NEXT_PUBLIC_META_PIXEL_ID'

const publicUrlEnvValues: Record<PublicUrlEnvName, string | undefined> = {
  NEXT_PUBLIC_CDN_BASE_URL: process.env.NEXT_PUBLIC_CDN_BASE_URL,
  NEXT_PUBLIC_REVERSE_GEOCODE_URL: process.env.NEXT_PUBLIC_REVERSE_GEOCODE_URL,
  NEXT_PUBLIC_PHONE_REFRESH_TOKEN_URL: process.env.NEXT_PUBLIC_PHONE_REFRESH_TOKEN_URL,
  NEXT_PUBLIC_BLUE_DART_TRACKING_URL: process.env.NEXT_PUBLIC_BLUE_DART_TRACKING_URL,
  NEXT_PUBLIC_HELP_CENTER_URL: process.env.NEXT_PUBLIC_HELP_CENTER_URL,
  NEXT_PUBLIC_GOOGLE_PRIVACY_POLICY_URL: process.env.NEXT_PUBLIC_GOOGLE_PRIVACY_POLICY_URL,
  NEXT_PUBLIC_GOOGLE_ANALYTICS_OPTOUT_URL:
    process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_OPTOUT_URL,
  NEXT_PUBLIC_FACEBOOK_URL: process.env.NEXT_PUBLIC_FACEBOOK_URL,
  NEXT_PUBLIC_X_URL: process.env.NEXT_PUBLIC_X_URL,
  NEXT_PUBLIC_LINKEDIN_URL: process.env.NEXT_PUBLIC_LINKEDIN_URL,
  NEXT_PUBLIC_INSTAGRAM_URL: process.env.NEXT_PUBLIC_INSTAGRAM_URL,
  NEXT_PUBLIC_SUPPORT_EMAIL: process.env.NEXT_PUBLIC_SUPPORT_EMAIL,
  NEXT_PUBLIC_GRIEVANCE_EMAIL: process.env.NEXT_PUBLIC_GRIEVANCE_EMAIL,
  NEXT_PUBLIC_SUPPORT_PHONE_DISPLAY: process.env.NEXT_PUBLIC_SUPPORT_PHONE_DISPLAY,
  NEXT_PUBLIC_SUPPORT_PHONE_NUMBER: process.env.NEXT_PUBLIC_SUPPORT_PHONE_NUMBER,
  NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
  NEXT_PUBLIC_GTM_SCRIPT_URL: process.env.NEXT_PUBLIC_GTM_SCRIPT_URL,
  NEXT_PUBLIC_GTM_NOSCRIPT_URL: process.env.NEXT_PUBLIC_GTM_NOSCRIPT_URL,
  NEXT_PUBLIC_GA_MEASUREMENT_ID: process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID,
  NEXT_PUBLIC_GA_SCRIPT_URL: process.env.NEXT_PUBLIC_GA_SCRIPT_URL,
  NEXT_PUBLIC_META_PIXEL_ID: process.env.NEXT_PUBLIC_META_PIXEL_ID,
}

function readEnv(name: PublicUrlEnvName): string | null {
  const value = publicUrlEnvValues[name]?.trim()
  return value ? value : null
}

function readLinkEnv(name: PublicUrlEnvName): string {
  return readEnv(name) ?? '#'
}

function readTextEnv(name: PublicUrlEnvName): string {
  return readEnv(name) ?? ''
}

function requireEnv(name: PublicUrlEnvName): string {
  const value = readEnv(name)
  if (!value) {
    throw new Error(`${name} is not set in the environment variables`)
  }

  return value
}

function trimTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '')
}

function withLeadingSlash(value: string): string {
  return value.startsWith('/') ? value : `/${value}`
}

function isAbsoluteUrl(value: string): boolean {
  return /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(value)
}

function mailtoHref(email: string): string {
  return email ? `mailto:${email}` : '#'
}

function telHref(phoneNumber: string): string {
  return phoneNumber ? `tel:${phoneNumber}` : '#'
}

export function buildCdnUrl(path: string): string {
  if (!path) {
    return path
  }

  if (isAbsoluteUrl(path)) {
    return path
  }

  const baseUrl = readEnv('NEXT_PUBLIC_CDN_BASE_URL')
  return baseUrl
    ? `${trimTrailingSlash(baseUrl)}${withLeadingSlash(path)}`
    : withLeadingSlash(path)
}

export const EXTERNAL_LINKS = {
  reverseGeocode: readLinkEnv('NEXT_PUBLIC_REVERSE_GEOCODE_URL'),
  blueDartTracking: readLinkEnv('NEXT_PUBLIC_BLUE_DART_TRACKING_URL'),
  helpCenter: readLinkEnv('NEXT_PUBLIC_HELP_CENTER_URL'),
  googlePrivacyPolicy: readLinkEnv('NEXT_PUBLIC_GOOGLE_PRIVACY_POLICY_URL'),
  googleAnalyticsOptOut: readLinkEnv('NEXT_PUBLIC_GOOGLE_ANALYTICS_OPTOUT_URL'),
} as const

export function getPhoneRefreshTokenUrl(): string {
  return requireEnv('NEXT_PUBLIC_PHONE_REFRESH_TOKEN_URL')
}

export const CONTACT_DETAILS = {
  supportEmail: readTextEnv('NEXT_PUBLIC_SUPPORT_EMAIL'),
  supportEmailHref: mailtoHref(readTextEnv('NEXT_PUBLIC_SUPPORT_EMAIL')),
  grievanceEmail: readTextEnv('NEXT_PUBLIC_GRIEVANCE_EMAIL'),
  grievanceEmailHref: mailtoHref(readTextEnv('NEXT_PUBLIC_GRIEVANCE_EMAIL')),
  supportPhoneDisplay: readTextEnv('NEXT_PUBLIC_SUPPORT_PHONE_DISPLAY'),
  supportPhoneHref: telHref(readTextEnv('NEXT_PUBLIC_SUPPORT_PHONE_NUMBER')),
} as const

export const SOCIAL_LINKS = {
  facebook: readLinkEnv('NEXT_PUBLIC_FACEBOOK_URL'),
  x: readLinkEnv('NEXT_PUBLIC_X_URL'),
  linkedin: readLinkEnv('NEXT_PUBLIC_LINKEDIN_URL'),
  instagram: readLinkEnv('NEXT_PUBLIC_INSTAGRAM_URL'),
} as const

export const ANALYTICS_CONFIG = {
  gtmId: readEnv('NEXT_PUBLIC_GTM_ID'),
  gtmScriptUrl: readEnv('NEXT_PUBLIC_GTM_SCRIPT_URL'),
  gtmNoscriptUrl: readEnv('NEXT_PUBLIC_GTM_NOSCRIPT_URL'),
  gaMeasurementId: readEnv('NEXT_PUBLIC_GA_MEASUREMENT_ID'),
  gaScriptUrl: readEnv('NEXT_PUBLIC_GA_SCRIPT_URL'),
  metaPixelId: readEnv('NEXT_PUBLIC_META_PIXEL_ID'),
} as const

export function hasAnalyticsConfig(): boolean {
  return Boolean(
    ANALYTICS_CONFIG.gtmId &&
      ANALYTICS_CONFIG.gtmScriptUrl &&
      ANALYTICS_CONFIG.gtmNoscriptUrl,
  )
}

export function hasMetaPixelConfig(): boolean {
  return Boolean(ANALYTICS_CONFIG.metaPixelId)
}
